import React, { useState, useEffect } from "react";
import "./HorizontalBarChart.scss";
import ItemService from "../../services/supplier_appCommonService";
import { FALLBACK_COLORS, STATUS_COLORS } from "./chartColors";

const HorizontalBarChart = ({
  queryName,
  fetchFunction,
  data,
  title = "Bar Chaty",
  keyName = null,

  // ✅ Dynamic color mapping from parent
  statusColors = STATUS_COLORS,
}) => {
  const [loading, setLoading] = useState(true);
  const [stageData, setStageData] = useState([]);

  /* -------------------- DATA PROCESSING -------------------- */
  useEffect(() => {
    let mounted = true;

    // const processData = (combined) => {
    //   let transformedData = [];

    //   if (keyName && combined?.length > 0) {
    //     const groupedData = combined.reduce((acc, item) => {
    //       const key = item[keyName];
    //       if (!key) return acc;

    //       if (!acc[key]) {
    //         acc[key] = { name: key, count: 0, id: key };
    //       }
    //       acc[key].count++;
    //       return acc;
    //     }, {});

    //     transformedData = Object.values(groupedData).map((item) => ({
    //       name: item.name,
    //       value: item.count,
    //       id: item.id,
    //     }));
    //   } else {
    //     transformedData =
    //       combined?.map((item) => ({
    //         name: item.name,
    //         value: item.count,
    //         id: item.id,
    //       })) || [];
    //   }

    //   setStageData(transformedData);
    // };

    const processData = (combined) => {
  let transformedData = [];

  if (
    Array.isArray(combined) &&
    combined.length > 0 &&
    combined[0]?.count !== undefined &&
    combined[0]?.status !== undefined
  ) {
    transformedData = combined.map((item, index) => ({
      name: item.status || item.name || title,
      value: item.count ?? 0,
      id: item.id || index,
    }));
  }

  else if (keyName && combined?.length > 0) {
    const groupedData = combined.reduce((acc, item) => {
      const key = item[keyName];
      if (!key) return acc;

      if (!acc[key]) {
        acc[key] = {
          name: key,
          count: 0,
          id: key,
        };
      }

      acc[key].count++;
      return acc;
    }, {});

    transformedData = Object.values(groupedData).map((item) => ({
      name: item.name,
      value: item.count,
      id: item.id,
    }));
  }
  else {
    transformedData =
      combined?.map((item, index) => ({
        name: item.name || "Unknown",
        value: item.count ?? item.value ?? 0,
        id: item.id || index,
      })) || [];
  }

  setStageData(transformedData);
};
    const fetchAllData = async () => {
      try {
        setLoading(true);

        if (data && Array.isArray(data)) {
          processData(data);
          return;
        }

        let page = 1;
        let per_page = 200;
        let combined = [];
        let hasMore = true;

        while (hasMore && mounted) {
          let res = fetchFunction
            ? await fetchFunction({ page, per_page })
            : await ItemService.generateChartQuery({
                queryName,
                page,
                per_page,
              });

          if (res?.data?.length > 0) {
            combined = [...combined, ...res.data];
            hasMore = res.data.length === per_page;
            page++;
          } else {
            hasMore = false;
          }
        }

        if (mounted) processData(combined);
      } catch (err) {
        console.error("❌ Error fetching chart data:", err);
        if (mounted) setStageData([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchAllData();
    return () => (mounted = false);
    // eslint-disable-next-line
  }, [queryName, fetchFunction, keyName, data]);

  /* -------------------- HELPERS -------------------- */
  const formatValue = (count) =>
    count >= 1000 ? `${(count / 1000).toFixed(1)}k` : count.toString();

  const getTotalValue = () =>
    stageData.reduce((sum, stage) => sum + (stage.value || 0), 0);

  const getStagePercentage = (stageValue) => {
    const total = getTotalValue();
    return total > 0 ? (stageValue / total) * 100 : 0;
  };
  const getStageColor = (index, stageName) => {
    // 1️⃣ Exact match
    if (statusColors?.[stageName]) {
      return statusColors[stageName];
    }

    // 2️⃣ Case-insensitive match
    const normalized = stageName?.toLowerCase();
    const matchedKey = Object.keys(statusColors).find(
      (key) => key.toLowerCase() === normalized
    );
    if (matchedKey) return statusColors[matchedKey];

    // 3️⃣ Fallback
    return FALLBACK_COLORS[index % FALLBACK_COLORS.length];
  };

  /* -------------------- UI STATES -------------------- */
  if (loading) {
    return (
      <div className="deal-forecast-card">
        <div className="deal-forecast-header">
          <h3>{title}</h3>
        </div>
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (!stageData?.length) {
    return (
      <div className="deal-forecast-card">
        <div className="deal-forecast-header">
          <h3>{title}</h3>
          <button className="menu-btn">⋯</button>
        </div>
        <div className="no-data">No data available</div>
      </div>
    );
  }

  /* -------------------- RENDER -------------------- */
  return (
    <div className="deal-forecast-card">
      <div className="deal-forecast-header">
        <h3 className="deal-title">
          {title}
          <span className="total-count-circle">{getTotalValue()}</span>
        </h3>
        <button className="menu-btn">⋯</button>
      </div>

      <div className="stacked-bar-section">
        {/* VALUES */}
        <div className="stage-values">
          {stageData.map((stage, index) => (
            <div
              key={`value-${stage.id}`}
              className="stage-value"
              style={{ width: `${getStagePercentage(stage.value)}%` }}
            >
              {formatValue(stage.value)}
            </div>
          ))}
        </div>

        {/* BAR */}
        <div className="stacked-progress-bar">
          {stageData.map((stage, index) => (
            <div
              key={`segment-${stage.id}`}
              className="progress-segment"
              style={{
                width: `${getStagePercentage(stage.value)}%`,
                backgroundColor: getStageColor(index, stage.name),
              }}
            />
          ))}
        </div>

        {/* LEGEND */}
        <div className="stage-labels">
          {stageData.map((stage, index) => (
            <div key={`label-${stage.id}`} className="stage-label">
              <span
                className="bar-indicator"
                style={{
                  backgroundColor: getStageColor(index, stage.name),
                }}
              />
              <span className="stage-name">{stage.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HorizontalBarChart;
